import type { FC } from 'react'
import { Suspense, lazy, memo, useLayoutEffect, useRef } from 'react'

import { IconButton, OverlayLoader, Space } from '@procurify/ui'
import { type IMentionsInputProps, type MentionsInput } from '@procurify/ui'
import { debounce } from 'lodash'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { UserAPI } from '@webapp/api/UserAPI'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { type FeatureSwitches, type User } from '@webapp/models'
import { type Comment } from '@webapp/models/Comment'
import { getFullName } from '@webapp/utils/user'
import { ChatComment } from './components/ChatComment'
import messages from './messages'
import { ChatConversationStyled } from './styles/ChatConversationStyled'
import { ChatInputContainerStyled } from './styles/ChatInputContainerStyled'
import { ChatStyled } from './styles/ChatStyled'

const LazyMentionsInput = lazy(() =>
  import('@procurify/ui/src/blocks/MentionsInput').then(
    ({ MentionsInput: mentionsInput }) => ({
      default: mentionsInput,
    })
  )
)

export interface ChatSubmitActions {
  clear: () => void
}

export interface IChatProps {
  canReply?: boolean
  comments: Comment[]
  receiverID?: User['id']
  onChatSubmit?: (content: string, actions: ChatSubmitActions) => void
  isSubmitting?: boolean
  isLoading?: boolean
  readOnly?: boolean
}

export const Chat: FC<IChatProps> = memo((props: IChatProps) => {
  const {
    comments,
    receiverID,
    onChatSubmit,
    isLoading,
    isSubmitting,
    readOnly,
  } = props

  const intl = useIntl()
  const formatMedia = useFormatMedia()

  const { hide_user_email_on_comment_tag: hideEmailInComment } = useSelector<
    any,
    FeatureSwitches
  >(makeSelectFeatureSwitches())

  const conversationRef = useRef<HTMLDivElement>(null)
  const mentionInputRef = useRef<MentionsInput>(null)
  const lastMentionRequestRef = useRef(null)

  useLayoutEffect(() => {
    if (!conversationRef.current) return

    conversationRef.current.scrollTop = conversationRef.current.scrollHeight
  }, [comments])

  const loadMentions: IMentionsInputProps['loadSuggestions'] = (
    searchTerm,
    suggestions,
    done
  ) => {
    const newRequest = {}
    lastMentionRequestRef.current = newRequest

    UserAPI.getBasicList({
      page_size: 10,
      order: '-name',
      search: searchTerm,
    })
      .then((res) => res.data)
      .then((users) => {
        if (lastMentionRequestRef.current !== newRequest) return

        done(
          users.map((user) => ({
            name: getFullName(user),
            profileImage: formatMedia(user.profile_image),
            id: user.id,
            description: hideEmailInComment ? '' : user.email,
          }))
        )
      })
  }

  const debouncedLoadMentions = debounce(loadMentions, 100, {
    leading: true,
  })

  const onSend = () => {
    const value = mentionInputRef.current?.getTextValue()

    if (!value.trim()) return

    onChatSubmit(value.trim(), {
      clear: mentionInputRef.current?.clear,
    })
  }

  return (
    <ChatStyled>
      <ChatConversationStyled ref={conversationRef}>
        <Space>
          {comments.map((comment) => (
            <ChatComment
              comment={comment}
              key={comment.id}
              isReceiver={comment.userprofile.id === receiverID}
            />
          ))}
        </Space>
      </ChatConversationStyled>
      <OverlayLoader visible={isLoading} />
      {!readOnly && (
        <ChatInputContainerStyled>
          <Suspense
            fallback={
              <textarea
                className='mentions-input-placeholder'
                disabled
                placeholder={intl.formatMessage(
                  messages.mentionsInputPlaceholder
                )}
              />
            }
          >
            <LazyMentionsInput
              loadSuggestions={debouncedLoadMentions}
              onSubmit={onSend}
              ref={mentionInputRef}
              action={
                <IconButton
                  icon='pointer-up'
                  aria-label={intl.formatMessage(messages.sendBtnLabel)}
                  onClick={onSend}
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
              }
              intl={{
                strings: {
                  placeholder: intl.formatMessage(
                    messages.mentionsInputPlaceholder
                  ),
                  instruction: intl.formatMessage(
                    messages.mentionsInputInstruction
                  ),
                },
              }}
            />
          </Suspense>
        </ChatInputContainerStyled>
      )}
    </ChatStyled>
  )
})

Chat.defaultProps = {
  canReply: true,
  onChatSubmit: () => undefined,
  isLoading: false,
  isSubmitting: false,
  readOnly: false,
}
Chat.displayName = 'Chat'
